// ═══════════════════════════════════════════════════════════════════════════
// BONUS À L'EMPLOI — LE PLAFOND ANNUEL RÉEL DE L'IMPÔT (art. 289ter/1, al. 3 CIR 92)
// ───────────────────────────────────────────────────────────────────────────
// Le plafond annuel du volet fiscal du bonus à l'emploi vient de l'ARTICLE
// 289ter/1, ALINÉA 3, CIR 92 — et non de l'art. 289ter, comme l'écrivaient
// plusieurs surfaces de l'app. La loi du 15/07/2026 portant réforme de l'IPP
// (art. 96, 4°) le porte de 765 € à 970 €, applicable « à partir de l'exercice
// d'imposition 2027 » (art. 97). Exercice d'imposition 2027 = REVENUS 2026.
//
// 🔓 CE MODULE MODÉLISAIT AUSSI, JUSQU'AU 24/09/2026, L'ÉCART ENTRE CE PLAFOND
// ET CELUI QUE LE MOTEUR APPLIQUAIT AU PRÉCOMPTE (`plafondPrecompte`,
// `ecartPrecompteImpot`, `messageEcart`). Cette partie est retirée : le
// fondateur a tranché l'arbitrage que le module documentait (« aucun plafond
// au précompte », cf. payroll-engine.js#calcBonusEmploi) — il n'y a donc plus
// de plafond modélisé à comparer au plafond réel de l'impôt, et l'ancien
// message (« tu récupères l'écart à l'enrôlement ») serait désormais FAUX dans
// son sens : sans plafond au précompte, c'est l'inverse qui peut se produire
// pour les plus bas salaires (réduction cumulée > plafond annuel de l'impôt),
// et le mécanisme de régularisation dans ce cas n'a pas été vérifié sur un
// texte primaire (R2 bis) — donc rien n'est affirmé ici plutôt qu'une
// affirmation inversée et non sourcée.
//
// Ce qui RESTE, et reste vrai : le plafond légal de l'impôt lui-même — utile
// pour l'affichage informatif (`lois.js`, `vitrine`) et pour l'agent IA
// (`bonus-chomage-affiche.js#blocBonusEmploi`).
//
// PUR — aucune I/O, aucune constante en dur : tout vient de lois-belges.js (R1).
// Verrou : __tests__/bonus-emploi-plafond.test.js (exécute).
// ═══════════════════════════════════════════════════════════════════════════
import { LB } from '@/app/lib/lois-belges';

/** L'année de revenus d'une date ou d'une année. Rend null si illisible. */
export function anneeRevenus(dateOuAnnee) {
  if (dateOuAnnee == null) return null;
  if (typeof dateOuAnnee === 'number' && Number.isFinite(dateOuAnnee)) {
    return dateOuAnnee >= 1900 && dateOuAnnee <= 2200 ? Math.trunc(dateOuAnnee) : null;
  }
  const t = String(dateOuAnnee).trim();
  const m = /^(\d{4})/.exec(t);
  if (!m) return null;
  const a = Number(m[1]);
  return a >= 1900 && a <= 2200 ? a : null;
}

/**
 * Le plafond IPP applicable aux revenus d'une année donnée.
 * @returns {{montant:number, exerciceImposition:number, revenus:number, source:string}|null}
 */
export function plafondIPP(dateOuAnnee) {
  const an = anneeRevenus(dateOuAnnee);
  if (an == null) return null;
  const registre = LB?.pp?.bonusEmploiPlafondIPP;
  if (!Array.isArray(registre) || !registre.length) return null;
  // Le registre est daté par année de REVENUS. On prend l'entrée la plus récente
  // dont l'année de revenus est ≤ l'année demandée : une année postérieure au
  // dernier palier connu hérite de ce palier, jamais d'un plafond inventé.
  const tries = [...registre].sort((a, b) => a.revenus - b.revenus);
  let sel = null;
  for (const e of tries) { if (e.revenus <= an) sel = e; }
  if (!sel) return null;
  return {
    montant: sel.montant,
    exerciceImposition: sel.exerciceImposition,
    revenus: sel.revenus,
    source: LB?.pp?.bonusEmploiPlafondIPPSource || '',
  };
}

/** La référence légale EXACTE du plafond — art. 289ter/1, al. 3, pas 289ter. */
export const REF_PLAFOND = 'Art. 289ter/1, al. 3, CIR 92';

export default { anneeRevenus, plafondIPP, REF_PLAFOND };
